export interface ErrorDetails {
  message: string
  code?: string
  details?: Record<string, unknown>
  timestamp: string
}

export class AppError extends Error {
  public code?: string
  public details?: Record<string, unknown>
  public timestamp: string

  constructor(message: string, code?: string, details?: Record<string, unknown>) {
    super(message)
    this.name = 'AppError'
    this.code = code
    this.details = details
    this.timestamp = new Date().toISOString()
  }
}

export const createError = (message: string, code?: string, details?: Record<string, unknown>): AppError => {
  return new AppError(message, code, details)
}

export const handleApiError = (error: unknown): AppError => {
  if (error && typeof error === 'object' && 'code' in error) {
    const errorWithCode = error as { code: string; message?: string }
    switch (errorWithCode.code) {
      case 'NOT_FOUND':
        return createError('Resource not found', 'NOT_FOUND', { originalError: error })
      case 'DUPLICATE_ENTRY':
        return createError('Duplicate entry found', 'DUPLICATE_ENTRY', { originalError: error })
      case 'FOREIGN_KEY_VIOLATION':
        return createError('Referenced record not found', 'FOREIGN_KEY_VIOLATION', { originalError: error })
      case 'PERMISSION_DENIED':
        return createError('Insufficient permissions', 'PERMISSION_DENIED', { originalError: error })
      default:
        return createError(errorWithCode.message || 'API error occurred', 'API_ERROR', { originalError: error })
    }
  }
  
  const errorMessage = error && typeof error === 'object' && 'message' in error 
    ? (error as { message: string }).message 
    : 'An unexpected error occurred'
  
  return createError(errorMessage, 'UNKNOWN_ERROR', { originalError: error })
}

export const logError = (error: AppError, context?: string): void => {
  console.error(`[${context || 'App'}] Error:`, {
    message: error.message,
    code: error.code,
    details: error.details,
    timestamp: error.timestamp,
    stack: error.stack
  })
}

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof AppError) {
    return error.message
  }
  
  if (error instanceof Error) {
    return error.message
  }
  
  if (typeof error === 'string') {
    return error
  }
  
  return 'An unexpected error occurred'
}

export const isRetryableError = (error: AppError): boolean => {
  const retryableCodes = ['NETWORK_ERROR', 'TIMEOUT', 'RATE_LIMIT']
  return retryableCodes.includes(error.code || '')
}
